import { useState, useEffect } from "react";
import {
  IdCard, Loader2, Phone, Mail, MapPin, Briefcase, CalendarDays, Euro,
  Landmark, StickyNote, ShieldCheck, UserRound,
} from "lucide-react";
import { getMyProfile } from "../services/api";
import { CONTRACT_LABELS } from "../components/employees/EmployeeFormDialog";
import { ROLE_LABELS } from "../utils/tabs";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";

const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "—";

const Info = ({ icon: Icon, label, value, href, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    <span className="w-8 h-8 rounded-lg bg-[#26788A]/10 flex items-center justify-center shrink-0 mt-0.5">
      <Icon size={14} className="text-[#26788A]" />
    </span>
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">{label}</p>
      {href && value ? (
        <a href={href} className="text-sm font-medium text-[#26788A] hover:underline break-all">{value}</a>
      ) : (
        <p className="text-sm font-medium text-slate-800 break-words">{value || "—"}</p>
      )}
    </div>
  </div>
);

export default function MyProfilePage() {
  const { user } = useAuth();
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    getMyProfile()
      .then((r) => setEmp(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-4 lg:p-8 max-w-3xl mx-auto overflow-y-auto h-screen pb-24" data-testid="my-profile-page">
      <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mb-6">
        <IdCard size={24} className="text-[#26788A]" /> Mes infos
      </h1>
      <Link to="/mes-bulletins" className="inline-flex items-center gap-2 text-sm font-medium text-[#26788A] min-h-11 mb-4 hover:underline" data-testid="my-profile-payslips-link">Consulter mes bulletins de salaire</Link>

      {/* Compte utilisateur */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4" data-testid="my-account-card">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-3">Mon compte</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Info icon={UserRound} label="Nom" value={user?.name} />
          <Info icon={Mail} label="Identifiant" value={user?.email} />
          <Info icon={ShieldCheck} label="Rôle" value={ROLE_LABELS[user?.role] || user?.role} />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-slate-300" /></div>
      ) : notFound ? (
        <div className="text-center py-12 bg-white border border-dashed border-slate-200 rounded-xl">
          <p className="text-slate-500 text-sm">Aucune fiche employé n'est liée à votre compte.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="my-employee-card">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Ma fiche employé</p>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                {CONTRACT_LABELS[emp.contract_type] || emp.contract_type}
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Info icon={Briefcase} label="Poste" value={emp.position} testId="profile-position" />
              <Info icon={CalendarDays} label="Date d'embauche" value={fmtDate(emp.hire_date)} />
              {emp.end_date && <Info icon={CalendarDays} label="Fin de contrat" value={fmtDate(emp.end_date)} />}
              <Info icon={Phone} label="Téléphone" value={emp.phone} href={emp.phone ? `tel:${emp.phone.replace(/\s/g, "")}` : null} />
              <Info icon={Mail} label="Email" value={emp.email} href={emp.email ? `mailto:${emp.email}` : null} />
              <Info icon={MapPin} label="Adresse" value={[emp.address, emp.postal_code, emp.city].filter(Boolean).join(", ")} />
              <Info icon={CalendarDays} label="Date de naissance" value={fmtDate(emp.birth_date)} />
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-4">Rémunération & banque</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Info icon={Euro} label="Salaire brut mensuel" value={emp.salary_gross_monthly != null ? `${Number(emp.salary_gross_monthly).toLocaleString("fr-FR")} €` : "—"} testId="profile-salary" />
              <Info icon={Landmark} label="IBAN" value={emp.iban} />
            </div>
          </div>

          {emp.notes && (
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 flex items-center gap-1 mb-1"><StickyNote size={11} /> Notes</p>
              <p className="text-xs text-slate-600 whitespace-pre-wrap">{emp.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
